import React, {useState, useEffect, useCallback, useContext, useRef} from 'react';
import {Link, useNavigate} from 'react-router-dom';
import {FaEye, FaDownload, FaFileInvoice} from 'react-icons/fa';

import {UserContext} from '../../UserContext';
import {useHttpClient} from '../hooks/useHttpClient';
import {useNotificacoes} from '../hooks/useNotificacoes';
import Pagination from '../pagination/Pagination';
import {useHasRole} from '../hooks/useHasRole';

import styles from './ListarFaturas.module.css';
import styles_global from '../css/global.module.css';
import styles_content from '../css/main-content.module.css';

function ListarFaturas() {
    const {user} = useContext(UserContext);
    const navigate = useNavigate();
    const {get} = useHttpClient();
    const {exibirErro} = useNotificacoes();

    const [distribuidoras, setDistribuidoras] = useState([]);
    const [faturas, setFaturas] = useState([]);
    const [filtros, setFiltros] = useState({
        distribuidora: '',
        uc: '',
        mesRef: '',
        page: 0,
        pageSize: 10
    });
    const [totalPages, setTotalPages] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const inicializou = useRef(false);
    const podeImportar = useHasRole(['ADMINISTRADOR', 'USUARIO']);

    // proteger rota
    useEffect(() => {
        if (!user) navigate('/login');
    }, [user, navigate]);

    // carregar dados iniciais
    useEffect(() => {
        (async () => {
            try {
                const empresaId = user?.empresa?.id;
                const dataDistribuidoras = await get(`/hello-sol/faturas-upload/distribuidoras`);
                setDistribuidoras(dataDistribuidoras);

                // busca inicial sem filtros
                if (!inicializou.current) {
                    inicializou.current = true;
                    fetchFaturas(0);
                }
            } catch (err) {
                exibirErro('Erro ao carregar dados iniciais: ' + err.message);
            }
        })();
    }, []);

    const fetchFaturas = useCallback(async (page) => {
        setLoading(true);
        setError('');
        try {
            const params = new URLSearchParams({
                ...(filtros.distribuidora && {distribuidora: filtros.distribuidora}),
                ...(filtros.uc && {uc: filtros.uc}),
                ...(filtros.mesRef && {mesRef: filtros.mesRef}),
                idEmpresa: String(user?.empresa?.id || ''),
                page: page.toString(),
                pageSize: filtros.pageSize.toString(),
            }).toString();

            const resp = await get(`/hello-sol/faturas-upload/faturasCarregadas?${params}`);
            setFaturas(resp.content || []);
            setTotalPages(resp.totalPages ?? 0);
            setFiltros(f => ({...f, page}));
        } catch (err) {
            setError(err.message || 'Erro ao buscar faturas');
            exibirErro(err.message);
        } finally {
            setLoading(false);
        }
    }, [get, filtros, exibirErro, user?.empresa?.id]);

    const handleChange = (e) => {
        const {name, value} = e.target;
        setFiltros((prev) => ({...prev, [name]: value}));
    };

    const fmtInt = (v) =>
        v == null ? "-" : toNum(v).toLocaleString("pt-BR");

    const toNum = (v) =>
        v == null ? null : Number(String(v).replace(/[^\d.-]/g, ""));

    const handleSubmit = (e) => {
        e.preventDefault();
        fetchFaturas(0);
    };

    const handlePageChange = (page) => {
        if (page >= 0 && page < totalPages) {
            fetchFaturas(page);
        }
    };

    const formatMoney = (valor) => {
        if (valor == null) return '—';
        return Number(valor).toLocaleString('pt-BR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    };

    const formatPeriodo = (inicio, fim) => {
        if (!inicio && !fim) return '—';
        return `${inicio || '—'} a ${fim || '—'}`;
    };

    const getDistribuidoraDisplay = (nome) => {
        const dist = distribuidoras.find(d => d.name === nome);
        return dist ? `${dist.display} (${dist.uf})` : nome;
    };

    return (
        <div className={styles_global.container}>
            <h2>Consultar Faturas</h2>

            <form onSubmit={handleSubmit} className={styles.filterForm}>
                <div className={styles_content.formField}>
                    <label htmlFor="distribuidora">Distribuidora</label>
                    <select
                        id="distribuidora"
                        name="distribuidora"
                        value={filtros.distribuidora}
                        onChange={handleChange}
                    >
                        <option value="">Todas as Distribuidoras</option>
                        {distribuidoras.map(d => (
                            <option key={d.name} value={d.name}>
                                {d.display} ({d.uf})
                            </option>
                        ))}
                    </select>
                </div>

                <div className={styles_content.formField}>
                    <label htmlFor="uc">Código UC</label>
                    <input
                        id="uc"
                        type="text"
                        name="uc"
                        value={filtros.uc}
                        onChange={handleChange}
                        placeholder="Ex.: 12345678"
                        maxLength="20"
                    />
                </div>

                <div className={styles_content.formField}>
                    <label htmlFor="mesRef">Mês/Ano</label>
                    <input
                        id="mesRef"
                        type="text"
                        name="mesRef"
                        value={filtros.mesRef}
                        onChange={handleChange}
                        placeholder="MM/AAAA"
                        maxLength="7"
                    />
                </div>

                <div className={styles.botoesAbaixo}>
                    <button type="submit" disabled={loading}>
                        {loading ? 'Consultando...' : 'Consultar'}
                    </button>
                    {podeImportar && (
                        <button type="button" onClick={() => navigate('/home/importar-faturas')}>
                            Importar Faturas
                        </button>
                    )}
                </div>
            </form>

            {loading && <p className={styles_global.loading}>Carregando...</p>}
            {error && <p className={styles_global.errorMessage}>{error}</p>}

            {!loading && faturas.length > 0 && (
                <>
                    <div className={styles.tableContainer}>
                        <table className={styles.table}>
                            <thead>
                            <tr>
                                <th>Distribuidora</th>
                                <th>UC</th>
                                <th>Referência</th>
                                <th>Período</th>
                                <th>Vencimento</th>
                                <th>Consumo (kWh)</th>
                                <th>Valor (R$)</th>
                            </tr>
                            </thead>
                            <tbody>
                            {faturas.map((fatura, idx) => (
                                <tr key={fatura.id} className={idx % 2 === 0 ? 'even-row' : 'odd-row'}>
                                    <td>{getDistribuidoraDisplay(fatura.distribuidora || fatura.distribuidoraId)}</td>
                                    <td>{fatura.ucCodigo}</td>
                                    <td>{fatura.refMesAno}</td>
                                    <td>{formatPeriodo(fatura.dataLeituraAnterior, fatura.dataLeituraAtual)}</td>
                                    <td>{fatura.vencimento}</td>
                                    <td>{fmtInt(fatura.consumoMedidorKwh)}</td>
                                    <td>R$ {formatMoney(fatura.valorTotal)}</td>

                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>

                    <Pagination
                        currentPage={filtros.page}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                    />
                </>
            )}

            {!loading && faturas.length === 0 && inicializou.current && (
                <p className={styles_global.noData}>Nenhuma fatura encontrada.</p>
            )}
        </div>
    );
}

export default ListarFaturas;